// Brand Colors Configuration
// Single source of truth for all colors in the application
// This file is imported by tailwind.config.js to ensure consistency

export const brandColors = {
  // Primary brand colors (Blue)
  primary: {
    50: '#dcd5fb',   // Light blue-purple (light secondary)
    100: '#b8aef7',  // Lighter tint
    200: '#6576e6',  // Medium blue-purple (medium secondary)
    300: '#2538bc',  // primary blue
    400: '#2031a5',  // Generated dark shade
    500: '#1b298e',  // Generated dark shade
    600: '#172277',  // Generated dark shade
    700: '#121a60',  // Generated dark shade
    800: '#0e1349',  // Generated dark shade
    900: '#090c32',  // Generated dark shade
  },

  // Secondary colors (Blue-Purple palette)
  secondary: {
    50: '#f8f7fe',   // Very light tint of light blue-purple
    100: '#dcd5fb',  // light blue-purple
    200: '#b8aef7',  // Mid-tone between light and medium
    300: '#6576e6',  // medium blue-purple
    400: '#5765d1',  // Generated dark shade
    500: '#4954bc',  // Generated dark shade
    600: '#3b42a7',  // Generated dark shade
    700: '#2d3192',  // Generated dark shade
    800: '#1f207d',  // Generated dark shade
    900: '#110f68',  // Generated dark shade
  },

  // Success colors (Green)
  success: {
    50: '#f0fdf4',   // Very light green
    100: '#dcfce7',  // Light green
    200: '#bbf7d0',  // Lighter green
    300: '#86efac',  // Light green
    400: '#4ade80',  // Medium light green
    500: '#22c55e',  // Main green
    600: '#16a34a',  // Darker green
    700: '#15803d',  // Dark green
    800: '#166534',  // Darker green
    900: '#14532d',  // Very dark green
  },

  // Warning colors (Amber/Yellow)
  warning: {
    50: '#fffbeb',   // Very light amber
    100: '#fef3c7',  // Light amber
    200: '#fde68a',  // Lighter amber
    300: '#fcd34d',  // Light amber
    400: '#fbbf24',  // Medium light amber
    500: '#f59e0b',  // Main amber
    600: '#d97706',  // Darker amber
    700: '#b45309',  // Dark amber
    800: '#92400e',  // Darker amber
    900: '#78350f',  // Very dark amber
  },

  // Error colors (Red)
  error: {
    50: '#fef2f2',   // Very light red
    100: '#fee2e2',  // Light red
    200: '#fecaca',  // Lighter red
    300: '#fca5a5',  // Light red
    400: '#f87171',  // Medium light red
    500: '#ef4444',  // Main red
    600: '#dc2626',  // Darker red
    700: '#b91c1c',  // Dark red
    800: '#991b1b',  // Darker red
    900: '#7f1d1d',  // Very dark red
  },

  // Info/Accent colors (Blue-Purple - for UI highlights, info messages, and special statuses)
  info: {
    50: '#f3f0f3',   // Very light purple 
    100: '#dcd5fb',  // light blue-purple
    200: '#b8aef7',  // Mid-tone
    300: '#6576e6',  // medium blue-purple
    400: '#5765d1',  // Generated dark shade (matches secondary)
    500: '#4954bc',  // Generated dark shade
    600: '#3b42a7',  // Generated dark shade
    700: '#2d3192',  // Generated dark shade
    800: '#1f207d',  // Generated dark shade
    900: '#110f68',  // Generated dark shade
  },

  // Accent colors (same as info for consistency)
  accent: {
    50: '#f3f0f3',   // Very light purple 
    100: '#dcd5fb',  // light blue-purple
    200: '#b8aef7',  // Mid-tone
    300: '#6576e6',  // medium blue-purple
    400: '#5765d1',  // Generated dark shade (matches secondary)
    500: '#4954bc',  // Generated dark shade
    600: '#3b42a7',  // Generated dark shade
    700: '#2d3192',  // Generated dark shade
    800: '#1f207d',  // Generated dark shade
    900: '#110f68',  // Generated dark shade
  },

  // Neutral colors (Gray - for text, borders, backgrounds)
  neutral: {
    50: '#fffef7',   // off-white/cream (lightest)
    100: '#f5f4ed',  // Very light warm gray
    200: '#e8e7e0',  // Light warm gray
    300: '#d4d3cc',  // Lighter gray
    400: '#a8a7a0',  // Medium light gray
    500: '#7c7b74',  // Medium gray
    600: '#54534d',  // Darker gray
    700: '#3a3935',  // Dark gray
    800: '#282724',  // Darker gray
    900: '#1d1d1d',  // very dark gray (darkest)
  },

  // Very dark blue (use sparingly as accent/text)
  dark: {
    900: '#01156b',  // very dark blue
  },
};

// Helper function to generate CSS custom properties for all color scales
const generateColorCSS = (colorName: string, colorScale: Record<number, string>) => {
  return Object.entries(colorScale)
    .map(([shade, value]) => `    --brand-${colorName}-${shade}: ${value};`)
    .join('\n');
};

// CSS custom properties for easy theming
export const brandColorCSS = `
  :root {
${generateColorCSS('primary', brandColors.primary)}
${generateColorCSS('secondary', brandColors.secondary)}
${generateColorCSS('success', brandColors.success)}
${generateColorCSS('warning', brandColors.warning)}
${generateColorCSS('error', brandColors.error)}
${generateColorCSS('info', brandColors.info)}
${generateColorCSS('accent', brandColors.accent)}
${generateColorCSS('neutral', brandColors.neutral)}
${generateColorCSS('dark', brandColors.dark)}
  }
`;

export default brandColors;
